import IcDelete from '@assets/details-camera-icons/IcDelete';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

interface VRNInputsProps {
    vrns: string[];
    onChange: (vrns: string[]) => void;
}

export default function VRNInputs({ vrns, onChange }: VRNInputsProps) {
    const lastInputRef = useRef<HTMLInputElement>(null);

    const addVRNInput = () => {
        onChange([...vrns, '']);
    };

    const removeVRNInput = (index: number) => {
        const newVrns = vrns.filter((_, i) => i !== index);
        onChange(newVrns.length > 0 ? newVrns : ['']);

        if (newVrns.length === 1 && lastInputRef.current) {
            setTimeout(() => {
                lastInputRef.current?.focus();
            }, 0);
        }
    };

    const updateVRN = (index: number, value: string) => {
        const newVrns = [...vrns];
        newVrns[index] = value.toUpperCase();
        onChange(newVrns);
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addVRNInput();
        }

        if (e.key === 'Backspace' && vrns[index] === '' && vrns.length > 1) {
            e.preventDefault();
            removeVRNInput(index);
        }
    };

    useEffect(() => {
        if (vrns.length > 1 && lastInputRef.current) {
            lastInputRef.current.focus();
            lastInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [vrns.length]);

    return (
        <Stack>
            <Typography variant="h6">Vehicle registration numbers</Typography>

            <Box
                sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: 2,
                        mt: 2,
                        '@media (max-width: 960px)': {
                            gridTemplateColumns: 'repeat(4, 1fr)',
                        },
                        '@media (max-width: 600px)': {
                            gridTemplateColumns: 'repeat(2, 1fr)',
                        },
                    }}
                >
                    {vrns.map((vrn, index) => {
                        const totalItems = vrns.length;
                        const isLastRow = Math.floor(index / 3) === Math.floor((totalItems - 1) / 3);
                        const itemsInLastRow = totalItems % 3 === 0 ? 3 : totalItems % 3;
                        const isLastRowItem = isLastRow && index >= totalItems - itemsInLastRow;

                        let gridColumnSpan = 2;

                        if (isLastRowItem) {
                            if (itemsInLastRow === 1) {
                                gridColumnSpan = 6;
                            } else if (itemsInLastRow === 2) {
                                gridColumnSpan = 3;
                            }
                        }

                        return (
                            <Box
                                key={index}
                                sx={{
                                    gridColumn: `span ${gridColumnSpan}`,
                                    '@media (max-width: 960px)': {
                                        gridColumn: isLastRowItem && itemsInLastRow === 1 ? 'span 4' : 'span 2',
                                    },
                                    '@media (max-width: 600px)': {
                                        gridColumn: 'span 2',
                                    },
                                }}
                            >
                                <TextField
                                    fullWidth
                                    autoFocus
                                    inputRef={index === vrns.length - 1 ? lastInputRef : undefined}
                                    label={`VRN ${index + 1}`}
                                    value={vrn}
                                    onChange={(e) => updateVRN(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    placeholder="Enter VRN"
                                    InputProps={{
                                        endAdornment: vrns.length > 1 && (
                                            <IconButton
                                                onClick={() => removeVRNInput(index)}
                                                color="error"
                                                size="small"
                                                sx={{ mr: -1 }}
                                            >
                                                <IcDelete />
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Button onClick={addVRNInput} variant="outlined" size="small" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                Add another VRN
            </Button>
        </Stack>
    );
}
